import { FC, SVGProps, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Map, ControlPosition } from 'shared/map-exports';
import { NearMeOutlined, NearMe } from '@mui/icons-material';
import { debounce } from 'es-toolkit/compat';
import NearMeDisabledOutLined from 'assets/near_me_disabled.svg?react';
import { UserPosition } from 'shared/types';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { SvgIconTypeMap } from '@mui/material';
import { ENV } from 'shared/ENV';
import { useMapGLController } from 'features/mapping/hooks/useMapGLController/useMapGLController';
import { hasValidCoordinates } from 'shared/utils';
import { MapControlButton, useButtonGroupStyles } from '../MapControlButton';
import {
  BaseMapboxGLController,
  BaseMapboxGLControllerProps,
  ThemeMode,
} from '../BaseMapboxGLController';

type GeolocateControlProps = {
  userPosition: UserPosition;
  position?: ControlPosition | undefined;
  goToUserPosition: () => Promise<void>;
  forcedThemeMode?: ThemeMode;
  map?: Map;
};

const floatsAreEqual = (a: number, b: number, tolerance: number): boolean =>
  Math.abs(a - b) < tolerance;

const GeolocateControlContent = (props: GeolocateControlProps): JSX.Element => {
  const { map, userPosition, goToUserPosition } = props;
  const { t } = useTranslation();
  const { locationEnabled } = userPosition;

  const handleClick = useMemo(
    () => debounce(goToUserPosition, 350),
    [goToUserPosition],
  );

  const mapCenter = map?.getCenter();

  const hasUserPosition = hasValidCoordinates(userPosition);

  const isAtUserPosition =
    floatsAreEqual(userPosition?.lat ?? 0, mapCenter?.lat ?? 0, 0.0001) &&
    floatsAreEqual(userPosition?.lng ?? 0, mapCenter?.lng ?? 0, 0.0001);

  const getLocationIconComponent = ():
    | FC<SVGProps<SVGSVGElement>>
    | OverridableComponent<SvgIconTypeMap<object, 'svg'>> => {
    if (!hasUserPosition || !locationEnabled) {
      return NearMeDisabledOutLined;
    }

    if (!isAtUserPosition) {
      return NearMeOutlined;
    }

    return NearMe;
  };

  const { classes } = useButtonGroupStyles();

  return (
    <MapControlButton
      ariaLabel={t('map.controls.nearMe')}
      onClick={handleClick}
      Icon={getLocationIconComponent()}
      className={ENV.VITE_3D_MAPS_ENABLED ? classes.groupTopButton : undefined}
      size="small"
    />
  );
};

class MapboxGLGeolocateController extends BaseMapboxGLController<
  GeolocateControlProps & BaseMapboxGLControllerProps
> {
  content = (): JSX.Element => (
    <GeolocateControlContent map={this.map} {...this.props} />
  );
}

export const GeolocateControl = (props: GeolocateControlProps): null => {
  useMapGLController(props, MapboxGLGeolocateController);
  return null;
};
